import { getAccessToken } from "./auth";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:4100";

// ---------------------------------------------------------------------------
// Org selection — persisted in localStorage so it survives page reloads
// ---------------------------------------------------------------------------

const SELECTED_ORG_STORAGE_KEY = "sovra_selected_org_id";

let selectedOrgId: string | null = localStorage.getItem(SELECTED_ORG_STORAGE_KEY);

export function setSelectedOrgId(id: string | null) {
  selectedOrgId = id;
  if (id) {
    localStorage.setItem(SELECTED_ORG_STORAGE_KEY, id);
  } else {
    localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
  }
  // Notify pages so they refetch
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("sovra:org-changed", { detail: id }));
  }
}

export function getSelectedOrgId(): string | null {
  return selectedOrgId;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * `orgIdOverride` permite forzar el header X-Org-Id de un request específico
 * sin tocar `selectedOrgId` global. Útil cuando un form (ej. crear subscriber)
 * deja elegir la org dentro del propio dialog en vez de depender del selector
 * del header.
 */
async function authHeader(orgIdOverride?: string | null): Promise<Record<string, string>> {
  const token = await getAccessToken();
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const orgId = orgIdOverride ?? selectedOrgId;
  if (orgId) {
    headers["X-Org-Id"] = orgId;
  }
  return headers;
}

export interface ApiOptions {
  /** Forzar el X-Org-Id de esta request sin tocar el org seleccionado globalmente. */
  orgId?: string | null;
}

/**
 * Si la URL del browser tiene `?org_id=X`, lo propaga a cada request del backend
 * (sin pisar el org_id que el path ya pueda traer). El backend lo honra solo
 * para usuarios admin; para operator/viewer queda ignorado.
 */
function withOrgScope(path: string): string {
  if (typeof window === "undefined") return path;
  const org = new URLSearchParams(window.location.search).get("org_id");
  if (!org) return path;
  const sep = path.includes("?") ? "&" : "?";
  if (path.match(/[?&]org_id=/)) return path;
  return `${path}${sep}org_id=${encodeURIComponent(org)}`;
}

export async function apiGet<T>(path: string, opts?: ApiOptions): Promise<T> {
  const headers = await authHeader(opts?.orgId);
  const res = await fetch(`${BASE}${withOrgScope(path)}`, { headers });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as T;
}

export async function apiPost<T>(path: string, body: unknown, opts?: ApiOptions): Promise<T> {
  const headers = { ...(await authHeader(opts?.orgId)), "Content-Type": "application/json" };
  const res = await fetch(`${BASE}${withOrgScope(path)}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as T;
}

export async function apiPatch<T>(path: string, body: unknown, opts?: ApiOptions): Promise<T> {
  const headers = { ...(await authHeader(opts?.orgId)), "Content-Type": "application/json" };
  const res = await fetch(`${BASE}${withOrgScope(path)}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as T;
}

export async function apiDelete(path: string, opts?: ApiOptions): Promise<void> {
  const headers = await authHeader(opts?.orgId);
  const res = await fetch(`${BASE}${withOrgScope(path)}`, { method: "DELETE", headers });
  if (!res.ok) throw await apiError(res);
}

async function apiError(res: Response) {
  try {
    const body = await res.json();
    return new ApiError(body?.error?.code ?? "unknown", body?.error?.message ?? res.statusText, res.status);
  } catch {
    return new ApiError("unknown", res.statusText, res.status);
  }
}

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

export interface Service {
  /** uuid global, único en toda la tabla. Usar para joins internos / FK. */
  id: string;
  /** legible por humanos ("curp.nl"). Único por org. Usar en URLs y display. */
  slug: string;
  name: string;
  category: string;
  icon: string | null;
  org_id?: string | null;
  health?: ServiceHealth | null;
}

export interface ServiceHealth {
  service_id: string;
  status: string;
  latency_p50_ms: number | null;
  latency_p95_ms: number | null;
  error_rate_24h: number | null;
  requests_24h: number | null;
  uptime_30d: number | null;
  last_error: { action?: string; message?: string } | null;
  checked_at: string;
}

export interface Trace {
  correlation_id: string;
  org_id: string;
  tags: Record<string, string | number | boolean>;
  end_user_email: string | null;
  end_user_id_hash?: string | null;
  end_user_display_name?: string | null;
  state: "ok" | "slow" | "error" | "timeout" | "in_progress";
  duration_ms: number | null;
  service_ids: string[];
  started_at: string;
  ended_at: string | null;
  event_count: number;
}

export interface TraceEvent {
  id: string;
  trace_id: string;
  service_id: string;
  sequence: number;
  timestamp: string;
  action: string;
  level: "info" | "warn" | "error";
  duration_ms: number | null;
  metadata: Record<string, unknown>;
  request?: Record<string, unknown> | null;
  response?: Record<string, unknown> | null;
  error_stack?: string | null;
}

export interface Incident {
  id: string;
  org_id: string;
  title: string;
  severity: "informational" | "minor" | "major" | "critical";
  state: "investigating" | "identified" | "monitoring" | "resolved";
  affected_service_ids: string[];
  opened_at: string;
  resolved_at: string | null;
  message_public: string;
  created_by_id: string;
}

export interface Subscriber {
  id: string;
  org_id: string;
  /** null cuando el subscriber es solo un canal de Slack. */
  email: string | null;
  channels: string[];
  service_ids: string[] | null;
  severity_min: string;
  slack_webhook_url?: string | null;
  verified: boolean;
}

export interface SavedView {
  id: string;
  owner_id: string;
  name: string;
  resource: "traces" | "incidents" | "services";
  filters: Record<string, unknown>;
  shared: boolean;
}

export interface Organization {
  id: string;
  slug: string;
  name: string;
  inserted_at: string;
  updated_at?: string;
}

export interface OrgMembership {
  org_id: string;
  slug: string;
  name: string;
  role: "admin" | "operator" | "viewer";
}

export interface OrgMember {
  user_id: string;
  email: string;
  display_name: string | null;
  role: "admin" | "operator" | "viewer";
  assigned_at: string;
}

export interface User {
  id: string;
  email: string;
  org_id: string | null;
  roles: string[];
  available_orgs: OrgMembership[];
  is_platform_admin: boolean;
  roles_in_org?: Record<string, string[]>;
}
